export interface LineaFactura {
    id?: number;
    facturaId?: number;
    descripcion: string;
    cantidad: number;
    precioUnitario: number;
    impuesto: number;
    subtotal: number;
    total: number;
    hotel?: string;
    fechaServicio?: string | null;
    tipoServicio?: string;
    destino?: string;
    costoProveedor?: number;
    comision?: number;
    createdAt?: Date;
    updatedAt?: Date;
  }
  
  export interface Factura {
    id?: number;
    numero: string;
    titulo?: string;
    fecha: string;
    fechaServicio?: string;
    clientId: number;
    clientType?: 'individual' | 'empresa';
    estado: 'pendiente' | 'pagada' | 'anulada' | 'abonada';
    lineas: LineaFactura[];
    subtotal: number;
    impuestoTotal: number;
    total: number;
    abono?: number;
    saldoPendiente?: number;
    notas?: string;
    entregadoPor?: string;
    recibidoPor?: string;
    clienteNombre?: string;
    manualInvoice?: boolean;
    clienteNombreManual?: string;
    reservationId?: number | null;
    reservation?: {
      id: number;
      descuento: number;
      taxRate: number;
      type?: 'b2b' | 'b2c';
      agenciaComision?: number;
    } | null;
  }
  
  export interface FacturaFormValues {
    id?: number;
    numero: string;
    titulo?: string;
    fecha: string;
    clientId?: number;
    agenciaId?: number;
    reservationId?: number | null;
    reservaId?: number | null;
    tipoCliente: 'individual' | 'empresa' | 'manual';
    estado: 'pendiente' | 'pagada' | 'anulada' | 'abonada';
    items: FacturaItem[];
    subtotal: number;
    impuestos: number;
    total: number;
    notas?: string;
    entregadoPor?: string;
    recibidoPor?: string;
    nombreCompleto?: string;
    documentoIdentificacion?: string;
    razonSocial?: string;
    rncEmpresa?: string;
    abono?: number;
    saldoPendiente?: number;
    direccionCliente?: string;
    telefonoCliente?: string;
    emailCliente?: string;
    clienteNombre?: string;
    manualInvoice?: boolean;
    clienteNombreManual?: string;
  }  
  
  export interface FacturaItem extends Omit<LineaFactura, 'id' | 'facturaId' | 'createdAt' | 'updatedAt'> {
    id?: number;
    subtotal: number;
    total: number;
    costoProveedor?: number;
    comision?: number;
  }
  
  export interface Cliente {
    id: number;
    nombre: string;
    email: string;
    telefono: string;
    direccion: string;
    tipo: 'individual' | 'empresa';
    documentoIdentificacion?: string;
    rncEmpresa?: string;
  }
  
  export interface Reservation {
    id: number;
    type: 'b2c' | 'b2b';
    clienteId?: number;
    agenciaId?: number;
    estado: 'pending_payment' | 'partial_payment' | 'paid' | 'no_show' | 'completed';
    precioTotal: number;
    montoFacturado: number;
    saldoPendiente: number;
  }
  
  export type TipoCliente = 'individual' | 'empresa' | 'manual';
  export type ClientFieldName = 'nombreCompleto' | 'documentoIdentificacion' | 'razonSocial' | 'rncEmpresa';
  export type TabId = 'facturas' | 'ingresos' | 'cobros' | 'gastos' | 'reportes' | 'notas';
  
  export interface ReservaItemData {
    descripcion: string;
    cantidad: number;
    precioUnitario: number;
    impuesto: number;
    subtotal: number;
    total: number;
    hotel?: string;
    fechaServicio?: string;
    tipoServicio?: string;
    destino?: string;
    costoProveedor?: number;
    comision?: number;
  }
  
  // Helper function to map Factura to FacturaFormValues
  export const mapFacturaToFormValues = (factura: Factura): FacturaFormValues => {
    return {
      id: factura.id,
      numero: factura.numero,
      titulo: factura.titulo,
      fecha: factura.fecha,
      clientId: factura.clientId,
      tipoCliente: factura.clientType || 'individual',
      estado: factura.estado,
      items: factura.lineas.map(linea => ({
        ...linea,
        subtotal: linea.subtotal,
        total: linea.total
      })),
      subtotal: factura.subtotal,
      impuestos: factura.impuestoTotal,
      total: factura.total,
      notas: factura.notas,
      entregadoPor: factura.entregadoPor,
      recibidoPor: factura.recibidoPor,
      abono: factura.abono,
      saldoPendiente: factura.saldoPendiente,
      clienteNombre: factura.clienteNombre,
      manualInvoice: factura.manualInvoice,
      clienteNombreManual: factura.clienteNombreManual,
      reservationId: factura.reservationId
    };
  };  
  
  export interface Nota {
    id?: number;
    facturaId: number;
    facturaNumero?: string;
    tipo: 'credito' | 'debito';
    numero: string;
    fecha: string;
    monto: number;
    motivo: 'error' | 'descuento' | 'ajuste' | 'otro';
    motivoDetalle?: string;
    descripcion: string;
    estado: 'aplicada' | 'anulada';
    impactoSaldo: number;
    createdAt?: Date;
    updatedAt?: Date;
    clienteNombre?: string;
  }
  
  export interface NotaStats {
    totalNotas: number;
    notasDelMes: number;
    notasDelMesAnterior: number;
    porTipo: {
      credito: number;
      debito: number;
    };
    porMotivo: {
      error: number;
      descuento: number;
      ajuste: number;
      otro: number;
    };
    tendencias: {
      notas: number;
    };
  }
  
  export interface NotaFormValues {
    id?: number;
    facturaId: number;
    tipo: 'credito' | 'debito';
    numero: string;
    fecha: string;
    monto: number;
    motivo: 'error' | 'descuento' | 'ajuste' | 'otro';
    motivoDetalle?: string;
    descripcion: string;
    impactoSaldo: number;
  }
  
  export interface ExportNotasFilters {
    startDate: string;
    endDate: string;
    tipo: string;
    facturaNumero: string;
    motivo: string;
    facturaId?: number;
  }
  
  // Interfaces para Gastos
  export interface Gasto {
    id?: number;
    fecha: string;
    concepto: string;
    proveedorId?: number; 
    proveedorNombre?: string;
    categoria: string;
    monto: number;
    impuesto: number;
    total: number;
    comprobante?: string;
    notas?: string;
    esFrecuente?: boolean;
    createdAt?: Date;
    updatedAt?: Date;
  }
  
  export interface GastoFormValues {
    id?: number;
    fecha: string;
    concepto: string;
    proveedorId?: number;
    proveedorNombre?: string;
    proveedorManual?: string;
    categoria: string;
    monto: number;
    impuesto: number;
    total: number;
    comprobante?: string;
    notas?: string;
    esFrecuente?: boolean;
  }
  
  export interface GastoStats {
    totalGastos: number;
    gastosDelMes: number;
    gastosDelMesAnterior: number;
    porCategoria: {
      [key: string]: number;
    };
    tendencias: {
      gastos: number;
    };
    totalProveedores: number;
  }
  
  export interface ExportGastosFilters {
    startDate: string;
    endDate: string;
    categoria: string;
    proveedor: string;
    proveedorId?: number;
  }
  
  export interface Proveedor {
    id: number;
    nombre: string;
    rncEmpresa?: string;
    telefono?: string;
    email?: string;
    direccion?: string;
    createdAt?: Date;
    updatedAt?: Date;
  }  